// Global Instructions Rule Applied!
import React, { useEffect, useRef, useState } from 'react'
import { Divider, Select } from 'antd'

function FundColumnRender({
  value,
  record,
  isEditing,
  fundOptions,
  showAllFunds = false,
  setShowAllFunds = null,
  hiddenFundCount = 0,
  onFieldChange,
  onStartEditing,
  shouldFocus = false,
  hasError = false
}) {
  const selectRef = useRef(null)
  const [isOpen, setIsOpen] = useState(false)

  // Reset open state when not focusing
  useEffect(() => {
    if (!shouldFocus) {
      setIsOpen(false)
    }
  }, [shouldFocus])

  useEffect(() => {
    if (isEditing && shouldFocus && selectRef.current) {
      setTimeout(() => {
        if (selectRef.current && typeof selectRef.current.focus === 'function') {
          selectRef.current.focus()
        }
      }, 100)
    }
  }, [isEditing, shouldFocus])

  const selectFundOptions = fundOptions.map((option) => {
    // Handle both {id, name} and {value, label} formats
    const optionId = option.id ?? option.value
    const optionName = option.name ?? option.label
    return {
      label: optionName,
      value: optionId
    }
  })

  if (isEditing) {
    return (
      <div data-testid={`commitment-fund-${record.key}`}>
        <Select
          ref={selectRef}
          value={value}
          onChange={(fundValue) => {
            onFieldChange(record.key, 'fund', fundValue)
            setIsOpen(false) // Close dropdown after selection
          }}
          options={selectFundOptions}
          showSearch
          filterOption={(input, option) => option?.label?.toLowerCase().includes(input.toLowerCase())}
          placeholder='- Select Fund -'
          size='small'
          style={{ width: '100%' }}
          status={hasError ? 'error' : undefined}
          open={isOpen}
          onOpenChange={(open) => {
            setIsOpen(open)
          }}
          autoFocus={shouldFocus}
          optionLabelProp='label'
          // Fund picker gate (fund status, 2026-09-04): the way back to the
          // funds the gate hid. Only rendered when it hid something.
          popupRender={(menu) => (
            <>
              {menu}
              {hiddenFundCount > 0 && setShowAllFunds ? (
                <>
                  <Divider style={{ margin: '4px 0' }} />
                  <div className='px-2 pb-1'>
                    <button
                      type='button'
                      data-testid='fund-select-show-all'
                      className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left text-xs'
                      // onMouseDown + preventDefault: a plain onClick closes the
                      // dropdown before the state lands.
                      onMouseDown={(e) => {
                        e.preventDefault()
                        setShowAllFunds(!showAllFunds)
                      }}
                    >
                      {showAllFunds
                        ? 'Show active funds only'
                        : `Show all funds (${hiddenFundCount} closed)`}
                    </button>
                  </div>
                </>
              ) : null}
            </>
          )}
        />
      </div>
    )
  }

  // Handle both {id, name} and {value, label} formats
  const fundOption = fundOptions.find((option) => {
    const optionId = option.id ?? option.value
    return optionId === value
  })
  const displayValue = fundOption?.name ?? fundOption?.label ?? value ?? '-'

  return (
    <div
      className='inline-edit-label cursor-pointer hover:bg-gray-50 px-2 py-0.5 rounded text-sm leading-tight'
      style={{ minHeight: '16px' }}
      onClick={() => onStartEditing(record.key, 'fund')}
    >
      <span className={value ? '' : 'text-gray-400'}>{displayValue}</span>
    </div>
  )
}

function FundColumn(props) {
  return <FundColumnRender {...props} />
}

export const getFundColumnConfig = (props) => ({
  title: 'Fund',
  dataIndex: 'fundName',
  key: 'fund',
  width: '45%',
  render: (value, record) => {
    const errorKey = `${record.key}-errors`
    const hasError = props.validationErrors?.[errorKey]?.fund
    const shouldFocus = props.focusField === 'fund' && props.editingRowKey === record.key

    return (
      <FundColumn
        value={record.fund}
        record={record}
        isEditing={props.editingRowKey === record.key}
        fundOptions={props.getAvailableFundOptions(record.key)}
        showAllFunds={props.showAllFunds}
        setShowAllFunds={props.setShowAllFunds}
        hiddenFundCount={props.hiddenFundCount}
        onFieldChange={props.handleFieldChange}
        onStartEditing={props.startEditing}
        shouldFocus={shouldFocus}
        hasError={hasError}
      />
    )
  }
})
